import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { supabase, seedUserDefaults } from '../lib/supabase';
import { fetchExchangeRates } from '../lib/exchangeRates';
import { invalidateAll } from '../lib/dataCache';
import { fullSync, deltaSync, needsFullSync, clearOnLogout, onSyncComplete } from './syncEngine';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [session, setSession]           = useState(null);
  const [user, setUser]                 = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [authLoading, setAuthLoading]   = useState(true);
  const [mfaRequired, setMfaRequired]   = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [syncVersion, setSyncVersion]   = useState(0); // bumped on each sync complete

  // Load exchange rates
  const loadRates = useCallback(async () => {
    setRatesLoading(true);
    try {
      const data = await fetchExchangeRates();
      setExchangeRates(data);
    } finally {
      setRatesLoading(false);
    }
  }, []);

  // Load user settings — read from local DB first (instant), network only as fallback
  const loadUserSettings = useCallback(async (userId) => {
    // Fast path: user_settings is a synced table, so it's already in IndexedDB
    // for any returning user. Reading it locally avoids a network round-trip
    // on every single app open.
    try {
      const { ldbGetAll } = await import('./localDB');
      const local = await ldbGetAll('user_settings');
      const localRow = local.find(r => r.user_id === userId);
      if (localRow) {
        setUserSettings(localRow);
        return; // done — no network call needed
      }
    } catch { /* fall through to network */ }

    // Slow path: brand new user, or local DB not ready yet
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code === 'PGRST116') {
      await seedUserDefaults(userId);
      const { data: seeded } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', userId)
        .single();
      setUserSettings(seeded);
    } else if (data) {
      setUserSettings(data);
    }
  }, []);

  const [syncReady, setSyncReady] = useState(false); // true once initial sync is done

  // Trigger sync when user is available
  const triggerSync = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const needs = await needsFullSync(userId);
      if (needs) {
        // Full sync — pages must wait for this to complete before reading local DB
        await fullSync(userId);
      } else {
        // Delta sync — local DB already has data, pages can render immediately
        setSyncReady(true);
        deltaSync(userId).catch(() => {});
        return; // syncReady already set above
      }
    } catch (e) {
      console.warn('[AppContext] Sync error:', e.message);
    } finally {
      setSyncReady(true);
    }
  }, []);

  // Auth state listener
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserSettings(session.user.id);
        triggerSync(session.user.id);
      }
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (event === 'SIGNED_IN' && session?.user) {
          await loadUserSettings(session.user.id);
          await triggerSync(session.user.id);
          setMfaRequired(false);
        }
        if (event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true);
        }
        if (event === 'SIGNED_OUT') {
          setUserSettings(null);
          setMfaRequired(false);
          setIsPasswordRecovery(false);
          await clearOnLogout();
        }
        if (event === 'MFA_CHALLENGE_VERIFIED') {
          setMfaRequired(false);
        }
        setAuthLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, [loadUserSettings, triggerSync]);

  // Delta sync on app focus (user switches back from another app)
  useEffect(() => {
    function handleFocus() {
      if (user?.id) deltaSync(user.id).catch(() => {});
    }
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible' && user?.id) {
        deltaSync(user.id).catch(() => {});
      }
    }
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id]);

  // Listen for sync completions and bump syncVersion to trigger re-renders
  useEffect(() => {
    const unsub = onSyncComplete(() => {
      setSyncVersion(v => v + 1);
      invalidateAll();
    });
    return unsub;
  }, []);

  // Load rates on mount and refresh every hour
  useEffect(() => {
    loadRates();
    const interval = setInterval(loadRates, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadRates]);

  // Update user settings
  const updateSettings = useCallback(async (updates) => {
    if (!user) return;
    const { data, error } = await supabase
      .from('user_settings')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .select()
      .single();
    if (!error && data) {
      setUserSettings(data);
      // Also write into local IndexedDB immediately — without this, the
      // in-memory React state updates instantly for THIS session, but a
      // restart before the next sync would read the stale local copy.
      try {
        const { ldbPut } = await import('./localDB');
        await ldbPut('user_settings', data);
      } catch (e) { /* local write is best-effort; next sync will catch up */ }
    }
    return { data, error };
  }, [user]);

  // Apply theme class to <html> whenever settings change
  useEffect(() => {
    const theme = userSettings?.theme || 'dark';
    const html = document.documentElement;
    html.classList.remove('theme-light', 'theme-amoled', 'theme-warm', 'theme-slate', 'theme-forest', 'theme-soft-light');
    if (theme !== 'dark') html.classList.add(`theme-${theme}`);
  }, [userSettings?.theme]);

  // Sign out
  const signOut = useCallback(async () => {
    invalidateAll();
    await supabase.auth.signOut();
  }, []);

  // Memoized so useApp() consumers only re-render when a field they could
  // read actually changes, instead of on every AppProvider render (e.g. the
  // hourly exchangeRates refresh or a syncVersion bump from a background sync).
  const value = useMemo(() => ({
    session, user, userSettings, exchangeRates, ratesLoading,
    authLoading, mfaRequired, setMfaRequired,
    isPasswordRecovery, clearPasswordRecovery: () => setIsPasswordRecovery(false),
    updateSettings, signOut, loadRates,
    syncVersion, syncReady,
    baseCurrency:        userSettings?.base_currency        || 'KWD',
    equivalentCurrency:  userSettings?.equivalent_currency  || 'USD',
    favouriteCurrencies: userSettings?.favourite_currencies || ['KWD','USD','EUR','GBP','TRY'],
    alertDays:           userSettings?.alert_days_ahead     || 5,
  }), [
    session, user, userSettings, exchangeRates, ratesLoading,
    authLoading, mfaRequired, isPasswordRecovery,
    updateSettings, signOut, loadRates,
    syncVersion, syncReady,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}

export default AppContext;
